import { useRef, useState } from "react";
import { here } from "../../desktop/desktop";
import DockWidget from "@common/components/DockWidget";
import { Icon, useConfirm } from "@jkast90/kast-common/ui";
import { useSession } from "@jkast90/kast-common/auth";
import { displayName } from "../saves";
import type { Emulator } from "../useEmulator";
import { CloudLibrary } from "./CloudLibrary";

interface LibraryProps {
  emu: Emulator;
  dock: HTMLElement | null;
}

export default function Library({ emu, dock }: LibraryProps) {
  const input = useRef<HTMLInputElement>(null);
  const isAuthenticated = useSession().status === "signedIn";
  const [showCloudLibrary, setShowCloudLibrary] = useState(false);
  const ready = emu.status === "ready";
  const [confirm, confirmDialog] = useConfirm();

  // Removes the ROM file only; its battery save and states stay on the device.
  const removeRom = async (name: string) => {
    const yes = await confirm({
      title: `Remove ${displayName(name)}?`,
      message: `This removes the ROM from ${here}. Its saves and states stay.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (yes) await emu.deleteRom(name);
  };

  const content = (
    <>
      {emu.library.length === 0 ? (
        <p className="muted">No games yet. Add a .gb, .gbc, .gba or .z64 file you own. It stays in {here}.</p>
      ) : (
        <ul className="rom-list">
          {emu.library.map((name) => (
            <li key={name} className={name === emu.current ? "current" : undefined}>
              <span className="name" title={name}>
                {displayName(name)}
              </span>
              <button type="button" className="btn" disabled={!ready} onClick={() => emu.play(name)}>
                {name === emu.current ? "Restart" : "Play"}
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={!ready}
                aria-label={`Remove ${displayName(name)}`}
                title={`Remove ${displayName(name)}`}
                onClick={() => void removeRom(name)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row library-actions">
        <button type="button" className="btn primary" disabled={!ready} onClick={() => input.current?.click()}>
          Add game…
        </button>
        {isAuthenticated && (
          <button
            type="button"
            className="btn"
            disabled={!ready}
            onClick={() => setShowCloudLibrary(true)}
            title="Load a ROM from cloud library"
          >
            <Icon name="backup" size={20} className="icon" /> Cloud…
          </button>
        )}
        <input
          ref={input}
          type="file"
          accept=".gb,.gbc,.gba,.z64,.n64,.v64,.zip,.7z"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) emu.addRom(file);
            e.target.value = "";
          }}
        />
      </div>
      {showCloudLibrary && <CloudLibrary emu={emu} onClose={() => setShowCloudLibrary(false)} />}
      {confirmDialog}
    </>
  );

  if (emu.current) {
    return (
      <DockWidget
        key={emu.current}
        dock={dock}
        label="Game"
        summary={
          <span className="name" title={emu.current}>
            {displayName(emu.current)}
          </span>
        }
        actions={
          <>
            <button
              type="button"
              className="btn icon-btn"
              aria-label={emu.paused ? "Resume" : "Pause"}
              title={emu.paused ? "Resume" : "Pause"}
              onClick={emu.togglePause}
            >
              <Icon name={emu.paused ? "play_arrow" : "pause"} size={20} className="icon" />
            </button>
            <button type="button" className="btn icon-btn" aria-label="Stop" title="Stop" onClick={emu.stop}>
              <Icon name="stop" size={20} className="icon" />
            </button>
          </>
        }
      >
        {content}
      </DockWidget>
    );
  }

  return (
    <section className="panel">
      <h2>Library</h2>
      {content}
    </section>
  );
}
